import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearch } from '@tanstack/react-router';
import { getVerifyCode, login, getUserInfo } from '@/lib/api/auth';
import { useAuthStore } from '@/store/auth-store';
import { cn, sha1Hex } from '@/lib/utils';

export function LoginPage() {
  const search = useSearch({ from: '/login' }) as {
    redirectPath?: string;
    redirectSearch?: string;
  };
  const setSession = useAuthStore((s) => s.setSession);
  const [username, setUsername] = useState('');
  const [pwd, setPwd] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyKey, setVerifyKey] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshCaptcha = useCallback(async () => {
    try {
      const { captcha: img, key } = await getVerifyCode();
      setCaptcha(img);
      setVerifyKey(key);
    } catch {
      setError('验证码加载失败,请重试');
    }
  }, []);

  useEffect(() => {
    void refreshCaptcha();
  }, [refreshCaptcha]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!username || !pwd || !verifyCode) {
      setError('请填写完整信息');
      return;
    }
    setLoading(true);
    try {
      const res = await login({ username, pwd: await sha1Hex(pwd), verifyCode, verifyKey });
      // Set token first so getUserInfo interceptor can attach it
      setSession(res.token, res.username ?? username);
      try {
        const userInfo = await getUserInfo();
        // Some backends only return permissionList; treat menuList as optional to
        // avoid throwing inside this catch and leaving permissions empty.
        const perms = userInfo.permissionList?.map((p) => p.permissionCode) ?? [];
        const menus = userInfo.menuList?.map((p) => p.permissionCode) ?? [];
        setSession(res.token, userInfo.username ?? res.username ?? username, perms, menus);
      } catch {
        // getUserInfo failed — token is already set, proceed with empty permissions
      }
      const targetPath = search.redirectPath ?? '/ads/dashboard';
      const href = search.redirectSearch
        ? `${targetPath}${search.redirectSearch.startsWith('?') ? '' : '?'}${search.redirectSearch}`
        : targetPath;
      // Use location.replace for post-login redirect: router.navigate does not
      // reliably resolve inline path params (e.g. /ads/accounts/1 vs $id).
      window.location.replace(href);
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败');
      void refreshCaptcha();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-6 shadow-sm"
        aria-label="登录"
      >
        <div>
          <h1 className="text-xl font-semibold">投放管理后台</h1>
          <p className="text-sm text-muted-foreground">请使用 AdsAdmin 独立账号登录</p>
        </div>
        <Field label="用户名">
          <input
            name="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="密码">
          <input
            name="pwd"
            type="password"
            autoComplete="current-password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="验证码">
          <div className="flex gap-2">
            <input
              name="verifyCode"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              className={cn(inputClass, 'flex-1')}
            />
            {captcha ? (
              <button
                type="button"
                onClick={refreshCaptcha}
                className="h-9 shrink-0 rounded-md border bg-muted"
                aria-label="刷新验证码"
              >
                <img src={captcha} alt="验证码" className="h-full w-auto" />
              </button>
            ) : (
              <div className="h-9 w-24 animate-pulse rounded-md bg-muted" aria-hidden />
            )}
          </div>
        </Field>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="h-9 w-full rounded-md bg-primary text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {loading ? '登录中…' : '登录'}
        </button>
      </form>
    </div>
  );
}

const inputClass =
  'h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5 text-sm">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
